/**
 * Formation intégrée — avancement de la prise en main (RPC formation_progress*).
 * Tenant et utilisateur résolus côté SQL depuis la session (invariant 4.A.10).
 */
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { ok, err, type Result } from '@/api/result';
import { appErrorFromSupabase, validationError, type AppError } from '@/api/errors';

const ProgressSchema = z.object({
  manager: z.boolean(),
  premiere_vente: z.boolean(),
  vente_carte: z.boolean(),
  cloture_z: z.boolean(),
  article: z.boolean(),
  imprimante: z.boolean(),
  equipe: z.boolean(),
});
export type FormationProgress = z.infer<typeof ProgressSchema>;

export type TeamMemberProgress = {
  readonly userId: string;
  readonly name: string;
  readonly premiereVente: boolean;
  readonly venteCarte: boolean;
  readonly clotureZ: boolean;
};

export const FormationApi = {
  async getProgress(): Promise<Result<FormationProgress, AppError>> {
    const { data, error } = await supabase.rpc('formation_progress');
    if (error) return err(appErrorFromSupabase(error, 'formation.progress'));
    const parsed = ProgressSchema.safeParse(data);
    if (!parsed.success) return err(validationError('formation_progress', parsed.error.message));
    return ok(parsed.data);
  },

  async getTeamProgress(): Promise<Result<TeamMemberProgress[], AppError>> {
    const { data, error } = await supabase.rpc('formation_progress_team');
    if (error) return err(appErrorFromSupabase(error, 'formation.team'));
    return ok(
      (data ?? []).map((r) => ({
        userId: r.user_id,
        name: r.display_name,
        premiereVente: r.premiere_vente,
        venteCarte: r.vente_carte,
        clotureZ: r.cloture_z,
      })),
    );
  },
};
